// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Box, Stack } from '@mantine/core';

import { OverlayScrollbar } from 'app/Layout/NavBar/NavBarContent/CustomScrollbar';
import { navbarTransitionStyle } from 'app/Layout/NavBar/useAppShell';

import classes from './SubNav.module.css';

type Props = {
  navbarExpanded: boolean;
  /** Whether the sub pane has slid in over the main pane. */
  pushed: boolean;
  mainPane: ReactNode;
  subPane: ReactNode;
};

type NavPaneProps = Pick<Props, 'navbarExpanded'> & {
  className?: string;
  offscreen: boolean;
  children: ReactNode;
};

const NavPane = ({ navbarExpanded, className, offscreen, children }: NavPaneProps) => (
  <Box className={[classes.pane, className].join(' ')} inert={offscreen}>
    <OverlayScrollbar
      className={navbarExpanded ? undefined : 'nav-bar-collapsed'}
      style={{ height: '100%', transition: navbarTransitionStyle }}
    >
      <Stack px={2} gap={4} className="nav-container-contents">
        {children}
      </Stack>
    </OverlayScrollbar>
  </Box>
);

/** Two stacked nav panes; pushing slides the sub pane in from the right over the main pane. */
export const NavPanes = ({ navbarExpanded, pushed, mainPane, subPane }: Props) => (
  <Box className={classes.viewport} mod={{ pushed }} flex="1 1 0">
    <NavPane navbarExpanded={navbarExpanded} className={classes.mainPane} offscreen={pushed}>
      {mainPane}
    </NavPane>
    <NavPane navbarExpanded={navbarExpanded} className={classes.subPane} offscreen={!pushed}>
      {subPane}
    </NavPane>
  </Box>
);
